/**
 * Mobile navigation drawer.
 *
 * THE GHOST CLICK. Mobile browsers replay a synthetic click roughly 300 ms
 * after a tap. When the toggle is tapped, that replayed click lands on the
 * scrim that just appeared underneath the finger and dismisses the drawer — so
 * the menu appears to never open at all. Two guards, both required:
 *
 *   1. incidental dismissals (scrim tap, outside click) are IGNORED for 400 ms
 *      after opening. Deliberate ones — the close button, Escape — stay
 *      immediate, because a user who reaches for them means it.
 *   2. `touch-action: manipulation` on the toggle in the template, which stops
 *      the browser waiting for a double-tap before dispatching at all.
 *
 * Focus is trapped while open and returned to the toggle on close: a drawer you
 * can tab out of leaves the keyboard user somewhere invisible behind the scrim.
 */
import { qs, qsa, on } from './dom.js';

const GHOST_CLICK_MS = 400;
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function setupDrawer() {
  const toggle = qs('[data-drawer-toggle]');
  const drawer = qs('[data-drawer]');
  if (!toggle || !drawer) return;

  const scrim = qs('[data-drawer-scrim]');
  const closers = qsa('[data-drawer-close]');

  let open = false;
  let openedAt = 0;

  function setOpen(next) {
    const wasOpen = open;
    open = next;
    openedAt = next ? Date.now() : 0;

    drawer.classList.toggle('is-open', next);
    drawer.setAttribute('aria-hidden', String(!next));
    toggle.setAttribute('aria-expanded', String(next));
    if (scrim) scrim.classList.toggle('is-open', next);

    // Locks the page behind the drawer. `overflow-hidden` is declared in
    // main.css via @source inline(), because no template contains it.
    document.body.classList.toggle('overflow-hidden', next);

    // Focus the panel itself (tabindex="-1"), not its first link: that is the
    // logo, and a ring on it reads as a stray selection. Tab still reaches it
    // first. Focus goes back to the toggle only when a real drawer closes —
    // the initial setOpen(false) at load must not steal focus.
    if (next) {
      drawer.focus?.();
    } else if (wasOpen) {
      toggle.focus?.();
    }
  }

  /** Scrim taps and outside clicks — subject to the ghost-click guard. */
  function dismissIncidental() {
    if (!open) return;
    if (Date.now() - openedAt < GHOST_CLICK_MS) return;
    setOpen(false);
  }

  on(toggle, 'click', (event) => {
    event.preventDefault();
    setOpen(!open);
  });

  if (scrim) on(scrim, 'click', dismissIncidental);

  // The close button is deliberate, so it bypasses the guard.
  for (const closer of closers) {
    on(closer, 'click', (event) => {
      event.preventDefault();
      setOpen(false);
    });
  }

  on(document, 'keydown', (event) => {
    if (!open) return;

    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (event.key !== 'Tab') return;

    // Focus trap. Without it Tab walks out of the drawer and onto the page
    // behind the scrim, where nothing is visible.
    const items = qsa(FOCUSABLE, drawer).filter((el) => el.offsetParent !== null);
    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    // Shift+Tab from the panel itself would otherwise leave the drawer.
    const atStart = document.activeElement === first || document.activeElement === drawer;
    if (event.shiftKey && atStart) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // A resize past the breakpoint leaves an open drawer stranded over a desktop
  // layout that has its own visible nav.
  on(window.matchMedia('(min-width: 64rem)'), 'change', (event) => {
    if (event.matches && open) setOpen(false);
  });

  setOpen(false);
}
